import { storage } from '@/services/storage'
import { deal, launchDay as launchdayService, customer } from '@/services/axios'
import { facilita } from '@/global'
import router from '@/routers'

import Pusher from 'pusher-js'

const definitions = storage.get('launchday_definitions')

const defaultControl = () => ({
    deal: null,
    checkins: null,
    timeline: null
})

export const launchDay = {
    namespaced: true,
    state: {
        config: null,
        pusher: null,
        create: false,
        steps: null,
        definitions: definitions ? JSON.parse(definitions) : null,
        preLoad: false,
        nextLoad: false,
        control: { ...defaultControl() }
    },
    getters: {
        getConfig(state) {
            return state.config
        },
        getSteps(state) {
            return state.steps
        },
        getCreate(state) {
            return state.create
        },
        getDefinitions(state) {
            return state.definitions
        },
        getPreLoad(state) {
            return state.preLoad
        },
        getNextLoad(state) {
            return state.nextLoad
        },
        getPusher(state) {
            return state.pusher
        },
        getControl(state) {
            return state.control
        }
    },
    mutations: {
        SET_CONFIG(state, payload) {
            state.config = payload
        },
        SET_PRE_LOAD(state, payload) {
            state.preLoad = payload
        },
        SET_STEPS(state, payload) {
            state.steps = payload
        },
        SET_DEFINITIONS(state, payload) {
            state.definitions = { ...payload }
        },
        CLEAR_DEFINITIONS(state) {
            state.definitions = null
        },
        SET_NEXT_LOAD(state, payload) {
            state.nextLoad = payload
        },
        SET_PUSHER(state) {
            state.pusher = new Pusher(facilita.pusherKey, { cluster: facilita.pusherCluster })
        },
        REMOVE_PUSHER(state) {
            state.pusher = null
        },
        SET_CONTROL_DEAL(state, payload) {
            state.control.deal = { ...payload }
            state.control.checkins = [...payload.checkins]
            state.control.timeline = { ...payload.timeline }
        },
        SET_LAUNCHDAY_BOX(state, payload) {
            state.definitions.launchday_box = { ...payload }
        },
        CLEAR_CONTROL(state) {
            state.control = { ...defaultControl() }
        }
    },
    actions: {
        setConfig({ commit }, payload) {
            commit('SET_CONFIG', payload)
        },
        setPreLoad({ commit }, payload) {
            commit('SET_PRE_LOAD', payload)
        },
        setSteps({ commit }, payload) {
            commit('SET_STEPS', payload)
        },
        setDefinitions({ commit }, payload) {
            commit('SET_DEFINITIONS', payload)
            storage.set('launchday_definitions', JSON.stringify(payload))
        },
        setNextLoad({ commit }, payload) {
            commit('SET_NEXT_LOAD', payload)
        },
        /**
         * Inicializa uma conexão com o servidor Pusher para utilizar o websocket
         */
        setPusher({ commit }) {
            commit('SET_PUSHER')
        },
        /**
         * Finaliza a conexão com o servidor Pusher
         */
        removePusher({ commit }) {
            commit('REMOVE_PUSHER')
        },
        /**
         * Seta os dados referentes ao cliente/negócio selecionado no controle
         * do lançamento
         */
        loadControlDeal({ commit, state }, payload) {
            let request = payload.type === 'cpf'
                ? customer.getAll('', { complete: true, q: payload.code })
                : deal.get(payload.code, 'infos')

            commit('SET_PRE_LOAD', true)
            return request.then(async response => {
                try {
                    // Pega os dados do negócio que foram retornados na requisição
                    let dealObject = null
                    /**
                     * A busca por CPF retorna um array de objetos, e caso nenhum item tenha sido encontrado
                     * deve-se emitir uma exceção indicando tal erro
                     */
                    if (payload.type === 'cpf') {
                        if (!response.data.length) {
                            response.data.msg = 'Não foi possível encontrar um negócio com o CPF informado.'
                            throw response
                        }
                        dealObject = { ...response.data[0] }

                    } else {
                        dealObject = { ...response.data }
                    }

                    const simulationResponse = await deal.get(dealObject.id, 'simulation')
                        .catch(error => {
                            let result = Promise.reject(error)
                            // Verifica se o erro é devido a falta de uma simulação
                            if (error.data &&
                                error.data.msg === 'Não foi possível encontrar uma simulação para o negócio.') {
                                result = { data: null }
                            }

                            return result
                        })

                    const checkinsResponse = await launchdayService.get('checkin/deal/', dealObject.id)
                    const checkins = checkinsResponse.data.filter(checkin => {
                        return parseInt(checkin.point.launchday_id) === parseInt(state.definitions.launchday.id)
                    })

                    const qrCodeResponse = await deal.getQRCode(dealObject.id)
                    const qrCodeSRC = qrCodeResponse.data

                    const timelineResponse = await deal.get(dealObject.id, 'simulatorProposal/timeline')
                    /**
                     * Agrupa, de acordo com os IDs das unidades, as simulações/propostas cadastradas para
                     * o negócio
                     */
                    let timeline = Object.values(timelineResponse.data)
                        .filter(item => item.type === 'simulation')
                        .reduce((groups, simulation) => {
                            const unitId = simulation.data && simulation.data.unity_id
                            if (unitId) {
                                groups[unitId] = groups[unitId] || []
                                groups[unitId].push(simulation)
                            }

                            return groups
                        }, Object.create(null))
                    // Mapeia para os IDs das unidades a última simulação cadastrada
                    for (const unitId in timeline) {
                        timeline[unitId] = timeline[unitId]
                            .sort((a, b) => new Date(b.date.date) - new Date(a.date.date))
                            .map(simulation => simulation.data)
                            .find(() => true)
                    }

                    /**
                     * Verifica se a regra que impossibilita aos clientes/negócios sem checkin no controle de
                     * chamado o acesso ao controle de reservas está ativa.
                     */
                    if (router.currentRoute.name === 'launch.control.reservation' &&
                        state.definitions.launchday.attributes.reserveOnlyToCalledCustom) {
                        /**
                         * Pega os IDs de todos os pontos de atendimento vinculados ao controle de chamado.
                         * Se um novo ponto foi configurado e o localStorage não foi atualizado, este novo
                         * ponto não será considerado
                         */
                        const calledPointIds = Object.values(state.definitions.launchday_points)
                            .filter(point => point.control.name === 'chamado')
                            .map(point => point.id)
                        /**
                         * Verifica se o cliente/negócio pesquisado possui um checkin válido em um dos
                         * controles de chamado
                         */
                        const calledCheckins = checkins.filter(checkin => {
                            return calledPointIds.includes(checkin.points_id) && checkin.situation === 'presente'
                        })

                        if (!calledCheckins.length) {
                            response.data.msg = 'Este negócio não possui checkin no controle de chamado!'
                            throw response
                        }
                    }

                    const controlDeal = {
                        ...dealObject,
                        qr_code: qrCodeSRC,
                        simulation: simulationResponse.data,
                        checkins,
                        timeline
                    }
                    commit('SET_CONTROL_DEAL', controlDeal)

                } catch (error) {
                    return Promise.reject(error)
                }
            }).finally(() => commit('SET_PRE_LOAD', false))
        },
        /**
         * Remove os dados do lançamento configurado do state e do localStorage
         */
        clearDefinitions({ commit }) {
            commit('CLEAR_DEFINITIONS')
            storage.remove('launchday_definitions')
        },
        /**
         * Recebe as informações referentes ao caixa configurado para o usuário logado e
         * as seta no state
         *
         * @param {Object} payload Informações de caixa do usuário logado
         */
        setLaunchdayBox({ commit }, payload) {
            commit('SET_LAUNCHDAY_BOX', payload)
        },
        /**
         * Limpa os dados referentes ao último controle utilizado
         */
        clearControl({ commit }) {
            commit('CLEAR_CONTROL')
        }
    }
}